/**
 * Api for platform middleware.
 */

import axios from 'axios';
import { UserFieldsFilter } from 'src/components/dialogs/UserFilter';
import { BackendResponse } from 'src/models';
import { IAffiliate } from 'src/models/affiliate';
import { IAppConfig } from 'src/models/appconfig';
import { GetBonusResponse } from 'src/models/bonus';
import { GetBonusCodeResponse } from 'src/models/bonuscode';
import { TransactionsResponse } from 'src/models/transaction';
import { GetUserResponse, GetUsersResponse } from 'src/models/users';
import { REACT_APP_API_PLATFORM_URL } from 'src/util/config';

const api = axios.create({
  baseURL: REACT_APP_API_PLATFORM_URL,
  timeout: 100000,
  withCredentials: true,
});

export const getAppConfigs = () => {
  return api.get<BackendResponse<IAppConfig[]>>('/api/v1/appconfig');
};

export const getSupportCases = (params: { page?: number; status?: string; type?: string; userId?: string }) => {
  return api.get('/api/v1/support', {
    params,
  });
};

export function changeFourthlineVerificationStatus(params: { status: string; userId: string }) {
  return api.put(`/api/v1/identification/support/fourthLineVerificationStatus/${params.userId}/${params.status}`);
}

export function getSupportCasesById(id: string) {
  return api.get('/api/v1/support/caseDetail/' + id);
}

export const patchRegistrationData = (changeData: { userId: string; firstName?: string; lastName?: string; phone?: string }) => {
  return api.patch('/api/v1/user/registrationData', changeData);
};

export const getUserDocument = (userId: string) => {
  return api.get(`/api/v1/user/userDocument?userId=${userId}`);
};

export const postSupportCaseInformation = (body: { supportCaseId: string; message: string }) => {
  return api.post('/api/v1/support/supportCase/information', body);
};

export const postSolveManualCheckSupportCase = (body: { supportCaseId: string; message: string }) => {
  return api.post('/api/v1/support/supportCase/solveIdentificationManualCheck', body);
};

export const postMangopayUploadDocumentManually = (formData: FormData) => {
  return api.post('/api/v1/support/supportCase/mangopayUploadDocumentManually', formData);
};

export const postSolveFailedPayoutSupportCase = (body: { supportCaseId: string; message: string; resourceId: string }) => {
  return api.post('/api/v1/support/supportCase/solvedFailedMangopayPayout', body);
};

export const startRestoreProcess = (body: { userId: string }) => {
  return api.post('/api/v1/support/userRestore', body);
};

export const deletePayoutAccounts = (userId: string) => {
  return api.delete(`/api/v1/support/user/${userId}/payoutAccounts`);
};

export const finishRestoreProcess = (body: { userId: string }) => {
  return api.post('/api/v1/support/userRestoreWallet', body);
};

export function getAffiliatePartners(params: { search?: string; page?: number }) {
  return api.get('/api/v1/affiliate', {
    params: {
      search: params?.search ? params.search : undefined,
      page: params?.page ? params.page : undefined,
    },
  });
}

export async function postAffiliate(affiliate: IAffiliate) {
  return api.post<BackendResponse<IAffiliate>>(`/api/v1/affiliate/${affiliate.partnerId}`, affiliate);
}

export function investedUsersReport({ partnerId, from, to }: { partnerId: string; from?: string; to?: string }) {
  return api.get(`api/v1/affiliate/invested-users/${partnerId}/csv`, {
    params: {
      from,
      to,
    },
  });
}

export function getAffiliatePartnersList(): Promise<BackendResponse<{ data: string[] }>> {
  return api.get('/api/v1/affiliate/partner/list');
}

export async function getPartners() {
  const res = await api.get('/api/v1/appconfig');
  return res.data.data;
}

type PartnerReportArg = { partnerId: string; from?: string | Date; to?: string | Date };
export const partnerReports = {
  transactions: ({ partnerId, from, to }: PartnerReportArg) => {
    return api.get('api/v1/transactions/reports/partner/', {
      params: {
        from,
        to,
        partnerId,
      },
    });
  },
  payins: ({ partnerId, from, to }: PartnerReportArg) => {
    return api.get('/api/v1/transactions/reports/partner/payin', {
      params: {
        from,
        to,
        partnerId,
      },
    });
  },
  assets: ({ partnerId, from, to }: PartnerReportArg) => {
    return api.get('/api/v1/transactions/reports/partner/partnerassets', {
      params: {
        from,
        to,
        partnerId,
      },
    });
  },
};

/**
 * Compliance year-end reports (platform-middleware `/api/v1/reporting`, role `complianceUser`).
 *
 * The `all*` variants are started detached on the backend and mail every affected user their
 * document, so they answer immediately with `{ started: true }` instead of waiting for the run.
 * `taxReport.forUser` also mails that one user; `exPost.forUser` only returns the PDF.
 */
export const complianceReports = {
  exPost: {
    forUser: ({ userId, year }: { userId: string; year: number }) => {
      return api.get<BackendResponse<{ userId: string; year: number; pdf: string }>>('/api/v1/reporting/exPostReportPdf', {
        params: { userId, year },
      });
    },
    forAllUsers: ({ year }: { year: number }) => {
      return api.get<BackendResponse<{ started: boolean; year: number; users: number }>>('/api/v1/reporting/allExPostReportPdf', {
        params: { year },
      });
    },
  },
  taxReport: {
    forUser: ({ userId, year }: { userId: string; year: number }) => {
      return api.get<BackendResponse<{ userId: string; year: number }>>('/api/v1/reporting/taxReportPdf', {
        params: { userId, year },
      });
    },
    forAllUsers: ({ year }: { year: number }) => {
      return api.get<BackendResponse<{ started: boolean; year: number }>>('/api/v1/reporting/allTaxReportPdf', {
        params: { year },
      });
    },
  },
};

export async function getPotentialUsers({ offset = 0, limit = 20, email }: { offset?: number; limit?: number; email?: string }) {
  const res = await api.get('/api/v1/support/potentialUsers', {
    params: {
      offset,
      limit,
      email,
    },
  });
  return res.data.data;
}

export function commissionReport({
  partnerId,
  from,
  to,
  commission,
}: {
  partnerId: string;
  from?: string;
  to?: string;
  commission: number;
}) {
  return api.get(`api/v1/affiliate/commission/${partnerId}/csv`, {
    params: {
      from,
      to,
      commission,
    },
  });
}

export const getUsersWithQuery = ({
  email,
  firstName,
  lastName,
  page = 0,
  userId,
  wallet,
  phone,
  referredBy,
  affiliatedBy,
}: {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  page?: number | null;
  userId?: string | null;
  phone?: string | null;
  wallet?: string | null;
  referredBy?: string | null;
  affiliatedBy?: string | null;
}) => {
  return api.get<BackendResponse<GetUsersResponse>>('/api/v1/user/search', {
    params: { email, firstName, lastName, page, userId, phone, wallet, referredBy, affiliatedBy },
  });
};

export const getUserWithQuery = (userId: string) => {
  return api.get<BackendResponse<GetUserResponse>>('/api/v1/user/search/' + userId);
};

export const getUserMessages = (userId: string, page = 0) => {
  return api.get<
    BackendResponse<
      {
        subject: string;
        ArrivedAt: string;
        Status: string;
        AttachmentCount: string;
        CampaignID: string;
      }[]
    >
  >(`/api/v1/user/search/${userId}/emails?page=${page}`);
};

export const getBonuses = (userId: string, page = 0) => {
  return api.get<BackendResponse<GetBonusResponse>>('/api/v1/bonus/', {
    params: { search: userId, page },
  });
};

export function getBonusCodes({ code, page = 0, expiresAt }: { code?: string; page?: number; expiresAt?: string }) {
  return api.get<BackendResponse<GetBonusCodeResponse>>('/api/v1/bonus/bonusCode', {
    params: { code, page, expiresAt },
  });
}

export function createBonusCode(bonusCode: any) {
  return api.post('/api/v1/bonus/bonusCode', bonusCode);
}

export function createBonus(bonus: { title: string; amount: number; userId: string }) {
  return api.post('/api/v1/support/userBonus', bonus);
}

export function changePayoutAccount(payoutInfo: { IBAN: string; userId: string }) {
  return api.post('/api/v1/support/userChangePaymentAccount', payoutInfo);
}

export function changeTaxID(body: { taxId: string; userId: string; country: string }) {
  return api.put('/api/v2/user/account/taxId', body);
}

export const getCSVReport = ({ registeredFrom, registeredTo }: { registeredFrom?: string; registeredTo?: string }) => {
  return api.get('/api/v1/user/report', {
    params: { registeredFrom, registeredTo },
  });
};

export function getIdentifications(params: { page: number } & Partial<UserFieldsFilter>) {
  return api.get('/api/v1/identification/support/users', {
    params,
  });
}

export function switchToIdentification(userId: string, type: 'IDNow' | 'Fourthline') {
  return api.put(`/api/v1/identification/support/identificationType/${userId}/${type}`);
}

export type Asset = {
  ISIN: string;
  address: string;
  apps: string[];
  availableBricks: number;
  availableBricksSecondary: number;
  bricks: number;
  campaignDate: string;
  center: { lat: number; lng: number };
  constructionStatus: string;
  createdAt: string;
  fullUrl: string;
  id: string;
  images: any[];
  investmentConditionsUrl: string;
  issuingSharePrice: number;
  likes: number;
  location: string;
  offers: any[];
  plannedFirstRentalIncomeDate: string;
  postalCode: string;
  propertyType: string;
  rentalPaymentDate: string;
  rentalReturn: number;
  securityType: string;
  sharePrice: number;
  stage: string;
  testing: false;
  title: string;
  updatedAt: string;
  url: string;
};

export function getAssets() {
  return api.get<{ data: Asset[] }>('/api/v1/assets/all');
}

export const getTransactions = ({
  page = 0,
  sort,
  dateMin,
  dateMax,
  type,
  method,
  status,
  userId,
}: {
  page?: number;
  sort?: TransactionSort;
  dateMin?: string;
  dateMax?: string;
  type?: TransactionType;
  method?: TransactionMethod;
  status?: TransactionStatus;
  userId?: any;
}): Promise<{ data: TransactionsResponse }> => {
  return api.get('/api/v1/transactions', {
    params: { page, sort, dateMin, dateMax, type, method, status, userId },
  });
};

export const getOffers = ({
  page = 0,
  offerId,
  assetId,
  firstParty,
  secondParty,
  status,
}: {
  page?: number | null;
  offerId?: string | null;
  assetId?: string | null;
  firstParty?: string | null;
  secondParty?: string | null;
  status?: OfferStatus | null;
}) => {
  return api.get('/api/v1/bulletinboard/offer/search', {
    params: { offerId, assetId, firstParty, secondParty, status, page },
  });
};

export const postRefund = (transactionId: string) => {
  return api.post(`/api/v1/payment/payin/refund/${transactionId}`);
};

export type SecurityNotice = {
  address: string;
  recoveryNotes: {
    date: string;
    locations: string[];
    content: string;
    _id: string;
  }[];
};

export function getSecurityNotice(walletAddress: string) {
  return api.get<BackendResponse<SecurityNotice>>(`/api/v1/support/securityNotice/${walletAddress}`);
}

/** Uploads a file (FormData field `file`) and returns a hosted url. */
export const postDataFile = (formData: FormData) => {
  return api.post<BackendResponse<{ url: string }>>('/api/v1/data/file', formData);
};

/** Creates a manual payout for a user. */
export const postManualPayout = (body: { userId: string; amount: number }) => {
  return api.post('/api/v1/support/manualPayout', body);
};

/** Deletes an accepted offer (only works when offer.status === 'accepted'). */
export const postDeleteAcceptedOffer = (body: { offerId: string }) => {
  return api.post('/api/v1/support/deleteAcceptedOffer', body);
};

/** Reverses a primary (OTC) buy transaction for a user/asset. */
export const postReversePrimaryForUser = (body: { userId: string; assetId: string }) => {
  return api.post('/api/v1/support/reversePrimaryForUser', body);
};

/**
 * Compliance-only: delete a user through the guarded deletion flow. Refuses with 400
 * `USER_HAS_ASSETS_OR_BALANCE` while the user still holds assets/balance; closes the Mangopay
 * account; a user with past transactions is retained (deactivated, state 'running') for statutory
 * retention. The deletion is recorded in the Deletions collection (visible in the deletions view).
 */
export const postDeleteUser = (body: { userId: string }) => {
  return api.post<BackendResponse<{ state?: string }>>('/api/v1/support/deleteUser', body);
};

// `skipMangopayUpdate` applies the change on the platform (+ compliance/Xpecto) only and does NOT
// push it to Mangopay — used when the Mangopay update is blocked (e.g. missing SCA consent) and the
// data does not need to be 100% in sync with Mangopay.
export const putUserEmail = (body: { emailOld: string; emailNew: string; skipMangopayUpdate?: boolean }) => {
  return api.put('/api/v1/support/userEmail', body);
};

export const putUserPhone = (body: { userId: string; phone: string; skipMangopayUpdate?: boolean }) => {
  return api.put('/api/v1/support/userPhone', body);
};

export const putUserName = (body: { userId: string; firstName: string; lastName: string; skipMangopayUpdate?: boolean }) => {
  return api.put('/api/v1/support/userName', body);
};

/** country must be an alpha3 country code. */
export const putUserAddress = (body: {
  userId: string;
  street: string;
  city: string;
  plz: string;
  country: string;
  skipMangopayUpdate?: boolean;
}) => {
  return api.put('/api/v1/support/userAddress', body);
};

/** Uploads a document (FormData field `file`) for a customer (customerId === userId) to Xpecto. */
export const postCustomerResidencyDoc = (customerId: string, formData: FormData) => {
  return api.post(`/api/v1/customer/${customerId}/residencyPermitAndUploadToXpecto`, formData);
};

export type Deletion = {
  userId: string;
  email: string;
  state: 'running' | 'closed';
  hadTransactions: boolean;
  systems: {
    authDeleted: boolean;
    customerDeleted: boolean;
    dbDeleted: boolean;
    mangopayClosed: boolean;
    mailjetDeleted: boolean;
  };
  createdAt: string;
  updatedAt: string;
};

export const getDeletions = (params: { status?: 'running' | 'closed'; skip?: number; limit?: number }) => {
  return api.get<BackendResponse<{ data: Deletion[]; count: number }>>('/api/v1/support/deletions', { params });
};

export type OfferStatus = 'init' | 'new' | 'accepted' | 'closed' | 'deleted';

export type TransactionStatus = 'init' | 'pending' | 'charged' | 'reserved' | 'success' | 'error' | 'canceled';

export type TransactionMethod = 'transfer' | 'account' | 'creditCard';

export type TransactionType = 'buy' | 'sell' | 'charge' | 'payout' | 'rent';

export type TransactionSort = 'oldest' | 'newest';
